import React from 'react';
import { Tabs } from 'react-tabs';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import styles from './styles.scss';
import { getGivenBackground } from 'Utils/getColor';
import { useWidth } from 'Utils/useWidth';
import { useScrollTo } from 'Utils/useScroll';
import ConsumptionData from 'Components/ConsumptionData';
import Dropdown from 'Components/Dropdown';

const cx = classNames.bind(styles);

class Tabbar extends React.Component {
  static propTypes = {
    id: PropTypes.string.isRequired,
    tabs: PropTypes.array.isRequired,
    children: PropTypes.node.isRequired,
    background: PropTypes.string,
    animate: PropTypes.bool,
    onChange: PropTypes.func,
    updateOneUrlQuery: PropTypes.func,
    defaultIndex: PropTypes.number,
    errors: PropTypes.array,
    className: PropTypes.string,
    // THIS IS ESSENTIAL if you use dropdowns in the children f.e. which require a rerender on state change
    rerenderMode: PropTypes.oneOf(['index', 'always', 'custom']),
    rerenderCheck: PropTypes.func,
    consumptionTextList: PropTypes.array,
    tabsHeaderClassName: PropTypes.string,
  };

  static defaultProps = {
    background: getGivenBackground('white'),
    defaultIndex: 0,
    animate: true,
    errors: [],
    rerenderMode: 'index',
    tabsHeaderClassName: '',
  };

  constructor(props) {
    super(props);

    this.state = {
      currentIndex: props.defaultIndex,
      isConsumptionInfoClosed: false,
    };

    // not in state since it's an ugly DOM operation which shouldn't cause rerenders :/
    this.scrollPositionIndex = props.defaultIndex > 0 ? props.defaultIndex - 1 : 0;
  }

  shouldComponentUpdate = (nextProps, nextState) => {
    const { rerenderMode, rerenderCheck, defaultIndex } = this.props;
    const { currentIndex, isConsumptionInfoClosed } = this.state;
    const defaultCondition = currentIndex !== nextState.currentIndex;

    if (
      defaultIndex !== nextProps.defaultIndex ||
      isConsumptionInfoClosed !== nextState.isConsumptionInfoClosed
    ) {
      return true;
    }

    switch (rerenderMode) {
      default:
        // index is default
        return defaultCondition;
      case 'always':
        return true;
      case 'custom':
        return (
          rerenderCheck({
            props: {
              prev: this.props,
              next: nextProps,
            },
            state: {
              prev: this.state,
              next: nextState,
            },
          }) || defaultCondition
        );
    }
  };

  onSelect = (onChange, index) => {
    if (onChange) {
      onChange(index);
    }

    this.scrollPositionIndex = index - 1;
    this.setState({
      currentIndex: index,
    });
  };

  componentDidMount() {
    this.forceUpdate();

    this.props.history.listen((location) => {
      const { hash } = location;
      if (hash && hash.startsWith('#highlights')) {
        const index = hash.split('-').pop() - 1;

        this.setState({
          currentIndex: index,
        });
      }
    });
  }

  componentDidUpdate(prevProps, prevState) {
    const { hash } = this.props.history.location;
    const { currentIndex } = this.state;

    if (currentIndex !== prevState.currentIndex) {
      this.openConsumptionInfo();
    }

    if (this.props.defaultIndex !== prevProps.defaultIndex) {
      this.setState({
        currentIndex: this.props.defaultIndex,
      });
    }

    if (!prevProps.animate && this.props.animate) {
      this.forceUpdate();
    }

    if (prevState.currentIndex !== this.state.currentIndex && this.props.updateOneUrlQuery) {
      this.props.updateOneUrlQuery(this.state.currentIndex, 'activetab');
    }

    if (hash && hash.startsWith('#highlights-')) {
      window.scrollTo(0, document.getElementById('highlights').offsetTop);
    }
  }

  toggleConsumptionInfo = () => {
    this.setState({ isConsumptionInfoClosed: !this.state.isConsumptionInfoClosed });
  };

  openConsumptionInfo = () => {
    if (this.state.isConsumptionInfoClosed) this.setState({ isConsumptionInfoClosed: false });
  };

  render() {
    const {
      id,
      tabs,
      children,
      background,
      onChange,
      errors,
      className,
      tabsHeaderClassName,
      consumptionTextList,
    } = this.props;

    const { currentIndex } = this.state;

    const tabbarClass = cx(styles.tabbar, {
      [background]: true,
      grey: getGivenBackground('grey') === background,
      white: getGivenBackground('white') === background,
      [className]: true,
    });

    /* via ref possible but a more complex way */
    const tlist = document.getElementById(id);
    const ctab = tlist ? tlist.childNodes[currentIndex] : null;

    /* check if childrensize > parentsize to display arrows */
    let childrenWidth = -1;
    const childrenPositions = [0]; // to add easy scroll handling

    if (tlist) {
      childrenWidth = 0;
      [...tlist.childNodes].forEach((node, _index) => {
        if (node.localName !== 'button') {
          return;
        }

        childrenWidth += useWidth(node);
        childrenPositions.push(childrenWidth);
      });

      if (childrenWidth < useWidth(tlist)) childrenWidth = -1;
    }

    // ensure that the current Tab receives focus
    setTimeout(() => {
      if (this.scrollPositionIndex >= 0 && this.scrollPositionIndex < childrenPositions.length) {
        useScrollTo(tlist, childrenPositions[this.scrollPositionIndex]);
      }
    }, 100);

    return (
      <Tabs
        className={tabbarClass}
        selectedTabClassName={styles.selected}
        selectedTabPanelClassName={styles.selectedPanel}
        onSelect={(index) => this.onSelect(onChange, index)}
        selectedIndex={currentIndex}
      >
        <Dropdown
          className={cx(styles.dropdown, { forceHide: tabs.length <= 2 })}
          label={'Option wählen*'}
          name={`${id}-dropwdown`}
          onChange={(index) => this.onSelect(onChange, index)}
          options={tabs.map((tab, tabIndex) => ({
            label: tab,
            value: tabIndex,
          }))}
          value={currentIndex}
        />
        <div
          className={cx(styles.head, {
            [tabsHeaderClassName]: true,
          })}
        >
          {childrenWidth > -1 && (
            <button
              className={styles.arrow}
              aria-label="Zeige vorherige Einträge"
              aria-hidden="true"
              tabIndex={-1}
              onClick={() => {
                if (this.scrollPositionIndex <= 0) {
                  return;
                }

                this.openConsumptionInfo();
                this.scrollPositionIndex -= 1;
                useScrollTo(tlist, childrenPositions[this.scrollPositionIndex]);
              }}
            >
              {'<'}
            </button>
          )}
          <div
            role="tablist"
            id={id}
            className={cx(styles.tablist, { [styles['arrows']]: childrenWidth > -1 })}
          >
            {tabs.map((data, i) => {
              const tabClass = cx(styles.tab, {
                error: errors.length > 0 && errors[i] === true,
              });

              return (
                <button
                  key={i}
                  className={tabClass}
                  role="tab"
                  aria-selected={currentIndex === i}
                  id={`${id}-tab-${i}`}
                  aria-controls={`${id}-panel-${i}`}
                  tabIndex={0}
                  onClick={() =>
                    this.setState({ currentIndex: i }, () => {
                      this.props.onChange && this.props.onChange(i);
                    })
                  }
                >
                  {data}
                </button>
              );
            })}

            <div
              className={styles.tabindicator_wrapper}
              style={{ width: childrenWidth === -1 ? 'auto' : childrenWidth }}
            >
              <div className={cx(styles.tabsubline, styles.tablist)} />
              <div
                className={styles.tabsubline_current}
                style={
                  ctab
                    ? {
                        transform: `translateX(${
                          ctab.offsetLeft - tlist.offsetLeft
                        }px) scaleX(${ctab.clientWidth / 100})`,
                      }
                    : {}
                }
              />
            </div>
          </div>
          {childrenWidth > -1 && (
            <button
              className={styles.arrow}
              aria-label="Zeige nachfolgende Einträge"
              aria-hidden="true"
              tabIndex={-1}
              onClick={() => {
                if (
                  this.scrollPositionIndex >= childrenPositions.length ||
                  childrenWidth - childrenPositions[this.scrollPositionIndex] <= useWidth(tlist)
                ) {
                  return;
                }

                this.openConsumptionInfo();
                this.scrollPositionIndex += 1;
                useScrollTo(tlist, childrenPositions[this.scrollPositionIndex]);
              }}
            >
              {'>'}
            </button>
          )}
        </div>

        {consumptionTextList && (
          <ConsumptionData
            compact
            consumptionText={consumptionTextList}
            style={{ marginTop: '20px' }}
          />
        )}

        {children.filter(Boolean).map((data, i) => {
          return (
            <div
              key={i}
              className={styles.tabpanel}
              role="tabpanel"
              hidden={i === currentIndex ? undefined : true}
              id={`${id}-panel-${i}`}
              aria-labelledby={`${id}-tab-${i}`}
            >
              {data}
            </div>
          );
        })}
      </Tabs>
    );
  }
}
export default withRouter(Tabbar);

export const triggerScrollEvent = () => {
  if (window.scrollY === undefined) {
    return;
  }
  const { scrollY } = window;
  window.scroll({ top: scrollY + 1 });
  window.scroll({ top: scrollY });
};
